const toSafeNumber = (value) => {
  const parsedValue = parseFloat(value);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const getSafeArray = (value) => (Array.isArray(value) ? value : []);

const DISC_PROFILE_COORDINATES = {
  1: { x: 35.49, y: 73.68 },
  2: { x: 27.94, y: 81.2 },
  3: { x: 23.37, y: 85.97 },
  4: { x: 21.76, y: 92.18 },
  5: { x: 35.49, y: 63.7 },
  6: { x: 3, y: 57.5 },
  7: { x: 0, y: 65.76 },
  8: { x: 85.44, y: 90.53 },
  9: { x: 72.93, y: 87.32 },
  10: { x: 80.89, y: 35.4 },
  11: { x: 78.92, y: 81.32 },
  12: { x: 57.84, y: 96.85 },
  13: { x: 95.92, y: 0.88 },
  14: { x: 37.04, y: 5.97 },
  15: { x: 99.44, y: 41.18 },
  16: { x: 2.83, y: 10.68 },
  17: { x: 72.93, y: 69.84 },
  18: { x: 62.74, y: 16.17 },
  19: { x: 34.44, y: 44.25 },
  20: { x: 91.82, y: 4.84 },
  21: { x: 72.93, y: 59.42 },
  22: { x: 78.92, y: 73.65 },
  23: { x: 62.74, y: 9.78 },
  24: { x: 87.63, y: 9 },
  25: { x: 21.95, y: 21.34 },
  26: { x: 39.67, y: 14.72 },
};

const getDiscClockwiseAngle = (profileId) => {
  const coordinates = DISC_PROFILE_COORDINATES[profileId];

  if (!coordinates) {
    return null;
  }

  const x = toSafeNumber(coordinates.x);
  const y = toSafeNumber(coordinates.y);

  // Angle reference: 0deg at top, grows clockwise.
  const angle = (Math.atan2(x - 50, y - 50) * 180) / Math.PI;
  return (angle + 360) % 360;
};

const sortByDiscApproximatePosition = (items = []) =>
  items
    .map((item, index) => ({
      ...item,
      __index: index,
      __discAngle: getDiscClockwiseAngle(item.id),
    }))
    .sort((a, b) => {
      const startAngle = 315;

      if (a.__discAngle === null && b.__discAngle === null) {
        return a.__index - b.__index;
      }

      if (a.__discAngle === null) {
        return 1;
      }

      if (b.__discAngle === null) {
        return -1;
      }

      const aOffset = (a.__discAngle - startAngle + 360) % 360;
      const bOffset = (b.__discAngle - startAngle + 360) % 360;

      return aOffset - bOffset;
    })
    .map(({ __index, __discAngle, ...item }) => item);

export const splitCenterLabel = (label) => {
  const trimmed = `${label || ''}`.trim();

  if (!trimmed) {
    return ['', ''];
  }

  if (trimmed.includes('/')) {
    const slashParts = trimmed
      .split('/')
      .map((part) => part.trim())
      .filter(Boolean);

    if (slashParts.length >= 2) {
      return [`${slashParts[0]}/`, slashParts.slice(1).join('/')];
    }
  }

  const words = trimmed.split(/\s+/);
  if (words.length < 2) {
    return [trimmed, ''];
  }

  const middleIndex = Math.ceil(words.length / 2);
  return [
    words.slice(0, middleIndex).join(' '),
    words.slice(middleIndex).join(' '),
  ];
};

export const buildProfileCategoriesDonutData = (
  profile = {},
  othersLabel = 'Others',
) => {
  const percentage = getSafeArray(profile?.percentage);
  const amount = getSafeArray(profile?.amount);

  const totalProfiles = amount.reduce(
    (total, item) => total + toSafeNumber(item?.total),
    0,
  );

  const hasProfilesData =
    amount.length > 0
      ? totalProfiles > 0
      : percentage.some((item) => toSafeNumber(item?.percentage) > 0);

  if (!hasProfilesData) {
    return {
      hasProfilesData: false,
      totalProfiles: 0,
      donutData: [
        {
          id: 'empty-profile-categories',
          label: '--',
          value: 100,
          total: 0,
          isEmpty: true,
        },
      ],
    };
  }

  const amountById = amount.reduce((map, item) => {
    if (item && item.id !== undefined && item.id !== null) {
      map[item.id] = toSafeNumber(item.total);
    }
    return map;
  }, {});

  const otherValue = percentage
    .slice(5)
    .reduce((total, item) => total + toSafeNumber(item?.percentage), 0);

  const otherValueTotal = amount
    .slice(5)
    .reduce((total, item) => total + toSafeNumber(item?.total), 0);

  const topCategories = sortByDiscApproximatePosition(
    percentage.slice(0, 5).map((item) => ({
      id: item.id,
      label: item.name,
      value: toSafeNumber(item.percentage),
      total: amountById[item.id] || 0,
    })),
  );

  return {
    hasProfilesData: true,
    totalProfiles,
    donutData: topCategories.concat({
        label: othersLabel,
        value: otherValue,
        isEmpty: true,
        total: otherValueTotal,
      }),
  };
};